import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, ChevronRight, Users } from 'lucide-react';
import Modal from '../ui/Modal';
import { getInitials, matchesStudentSearch } from '../../utils/stringUtils';
import { STUDENT_STATUS } from '../../utils/constants';
import { sortStudentsByName } from '../../utils/studentLogic';

/** Kaydırıldıkça ekrana basılan parça boyutu — ağ kotası değil, DOM yükü */
export const LIST_PAGE_SIZE = 25;

const matchesSearch = (student, term) =>
  matchesStudentSearch([student.name, student.userCode, student.emailOrPhone], term);

/**
 * Anasayfadaki "Tümünü Gör" penceresi.
 * Liste bellekteki öğrencilerden gelir; kaydırıldıkça parça parça DOM'a basılır.
 */
const TumOgrencilerModal = ({ isOpen, students, onClose, onSelectStudent }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleCount, setVisibleCount] = useState(LIST_PAGE_SIZE);
  const listRef = useRef(null);
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setVisibleCount(LIST_PAGE_SIZE);
    }
  }, [isOpen]);

  const filtered = useMemo(
    () => sortStudentsByName(students).filter((s) => matchesSearch(s, searchTerm)),
    [students, searchTerm]
  );

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visible.length < filtered.length;

  useEffect(() => {
    if (!isOpen || !hasMore) return undefined;

    const root = listRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisibleCount((prev) => Math.min(prev + LIST_PAGE_SIZE, filtered.length));
        }
      },
      { root, rootMargin: '80px', threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isOpen, hasMore, filtered.length, visible.length]);

  return (
    <Modal title="Tüm Öğrencilerim" isOpen={isOpen} onClose={onClose}>
      <div className="flex flex-col gap-3 min-h-0">
        <div className="relative shrink-0">
          <Search className="absolute left-3.5 top-3.5 text-slate-400" size={18} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setVisibleCount(LIST_PAGE_SIZE);
            }}
            placeholder="Ad, üye numarası veya e-posta ile ara..."
            aria-label="Öğrenci ara"
            className="w-full bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white pl-11 pr-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 outline-none focus:bg-white dark:focus:bg-slate-800 focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 transition-[border-color,box-shadow,background-color] duration-200 text-sm font-medium"
          />
        </div>

        <p className="text-xs font-bold text-slate-400 shrink-0">
          {searchTerm.trim()
            ? (hasMore
              ? `${visible.length} / ${filtered.length} sonuç`
              : `${filtered.length} sonuç`)
            : (hasMore
              ? `${visible.length} / ${filtered.length} öğrenci`
              : `${filtered.length} öğrenci`)}
        </p>

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-12 text-center">
            <Users size={28} className="text-slate-300 dark:text-slate-600" />
            <p className="text-sm font-bold text-slate-400">
              {students?.length
                ? 'Aramanızla eşleşen öğrenci bulunamadı.'
                : 'Henüz öğrenciniz yok.'}
            </p>
          </div>
        ) : (
          <ul
            ref={listRef}
            className="divide-y divide-slate-100 dark:divide-slate-800 border border-slate-100 dark:border-slate-800 rounded-2xl overflow-y-auto custom-scrollbar max-h-[min(60vh,28rem)]"
            aria-label="Öğrenci listesi"
          >
            {visible.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => onSelectStudent(s)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors"
                >
                  <span className="w-9 h-9 rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold text-xs border border-blue-100 dark:border-blue-800 shrink-0">
                    {getInitials(s.name)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-bold text-sm text-slate-800 dark:text-white truncate">
                      {s.name}
                      <span className="text-[11px] font-medium text-slate-400 ml-1.5">
                        #{s.userCode}
                      </span>
                    </span>
                    <span className="block text-[11px] text-slate-400 truncate">
                      {s.emailOrPhone || '—'}
                    </span>
                  </span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-bold border shrink-0 ${
                      s.status === STUDENT_STATUS.ACTIVE
                        ? 'bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800'
                        : 'bg-slate-50 text-slate-500 border-slate-200 dark:bg-slate-700 dark:text-slate-400 dark:border-slate-600'
                    }`}
                  >
                    {s.status}
                  </span>
                  <ChevronRight size={15} className="text-slate-300 dark:text-slate-600 shrink-0" />
                </button>
              </li>
            ))}
            {hasMore && (
              <li
                ref={sentinelRef}
                aria-hidden="true"
                className="h-8 flex items-center justify-center"
              >
                <span className="text-[10px] font-bold text-slate-300 dark:text-slate-600">
                  Kaydırarak devam…
                </span>
              </li>
            )}
          </ul>
        )}
      </div>
    </Modal>
  );
};

export default TumOgrencilerModal;
